import { useLogger } from "@amzn/xb-hooks";
import { useEffect, useRef } from "react";

const queuedCallbacks: Set<() => void> = new Set();

declare global {
  interface Window {
    AWSMA: {
      // eslint-disable-next-line @typescript-eslint/ban-types
      ready: Function;
      TRIGGER_EVENT: string;
    };
  }
}

/**
 * AWSMA is the object s-code emits as an "interface".
 *
 * There is a race-condition currently, if AWSMA has not been augmented with "ready" before
 * we attempt to send an event we fail. This proxy tracks "missed" events and fires them
 * after s_code has been initialized.
 *
 * We create a queuing mechanism that will keep track of "missed" events, that we rebroadcast
 * when the telemetry script (s-code) is ready.
 *
 * https://code.amazon.com/packages/AwsTcExperienceBridgeScripts/blobs/mainline/--/src/externalScripts/scripts/telemetry.ts
 * https://code.amazon.com/packages/AWSSiteCatalystJavascript/blobs/mainline/--/src/config.js
 */
const useCallbackQueue = () => {
  const { log } = useLogger();
  return {
    enqueue: (callback: () => void) => {
      log.debug("AWSMA not ready, queuing callback");
      queuedCallbacks.add(callback);
    },
    clear: () => {
      log.debug("AWSMA is ready, flushing queued callbacks");
      queuedCallbacks.forEach((callback) => window.AWSMA.ready(callback));
      queuedCallbacks.clear();
    },
  };
};

export const useAWSMA = () => {
  const { log } = useLogger();
  const { enqueue, clear } = useCallbackQueue();
  const elapsedTime = useRef(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      elapsedTime.current += 10;
      if (typeof window.AWSMA?.ready === "function") {
        clearInterval(intervalId);
        clear();
      } else if (elapsedTime.current > 1000) {
        log.fatal("exceeding maximum polling time while polling for window.AWSMA");
        clearInterval(intervalId);
      } else {
        log.debug("failed check while polling for window.AWSMA");
      }
    }, 10);
    return () => clearInterval(intervalId);
  }, [log, clear]);

  return {
    EVENT_NAME: window.AWSMA?.TRIGGER_EVENT ?? "custom_awsma_trigger",
    ready: (callback: () => void) => (window?.AWSMA?.ready ? window.AWSMA.ready(callback) : enqueue(callback)),
  };
};
